/**
 * Crew and vehicle allocation.
 *
 * The dispatcher's actual job. Two things it must do that the first version did
 * not: show what is still missing BEFORE a blocked button is clicked, and let a
 * mistake be undone. Assigning the wrong ambulance at 2am and having no way to
 * take it off is how a case ends up showing four vehicles.
 *
 * Anyone already on another open case is marked, not hidden. A dispatcher
 * sometimes genuinely needs to pull a crew off a lower-priority job, and a
 * system that removes the option just gets worked around by phone.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type CaseDetail, type Staff, type Vehicle } from '../api/client.js';
import { NearestVehicles } from './NearestVehicles.js';

const CREW_ROLES = [
  { value: 'crew_lead', label: 'Crew lead' },
  { value: 'crew', label: 'Crew' },
  { value: 'driver', label: 'Driver' },
];

const TIER_LABEL: Record<string, string> = {
  bls: 'BLS',
  ils: 'ILS',
  als: 'ALS',
  critical_care: 'CC',
  specialist: 'Spec',
};

function Tier({ tier }: { tier: string | null }) {
  if (!tier) return null;
  return (
    <span className="rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-medium text-slate-200">
      {TIER_LABEL[tier] ?? tier}
    </span>
  );
}

type Assignment = CaseDetail['assignments'][number];

function personName(a: Assignment): string {
  return `${a.first_name ?? ''} ${a.last_name ?? ''}`.trim();
}

export function AssignPanel({ caseId, assigned }: { caseId: string; assigned: Assignment[] }) {
  const qc = useQueryClient();
  const [role, setRole] = useState('crew');
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const staff = useQuery({
    queryKey: ['staff'],
    queryFn: () => api.get<{ staff: Staff[] }>('/staff'),
  });
  const vehicles = useQuery({
    queryKey: ['vehicles'],
    queryFn: () => api.get<{ vehicles: Vehicle[] }>('/vehicles'),
  });

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['case', caseId] }),
      qc.invalidateQueries({ queryKey: ['transitions', caseId] }),
      qc.invalidateQueries({ queryKey: ['nearest', caseId] }),
      qc.invalidateQueries({ queryKey: ['staff'] }),
      qc.invalidateQueries({ queryKey: ['vehicles'] }),
    ]);
  };

  const assign = useMutation({
    mutationFn: (body: { staffId?: string; vehicleId?: string; role: string }) =>
      api.post(`/cases/${caseId}/assignments`, body),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  /** Undo. The endpoint always existed; nothing in the UI called it. */
  const release = useMutation({
    mutationFn: (assignmentId: string) =>
      api.post(`/cases/${caseId}/assignments/${assignmentId}/release`),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const active = assigned.filter((a) => !a.released_at);
  const crew = active.filter((a) => a.staff_id);
  const fleet = active.filter((a) => a.vehicle_id);

  const assignedStaffIds = new Set(crew.map((a) => a.staff_id));
  const assignedVehicleIds = new Set(fleet.map((a) => a.vehicle_id));

  const busy = assign.isPending || release.isPending;
  const term = search.trim().toLowerCase();

  const visibleStaff = (staff.data?.staff ?? []).filter(
    (s) => !term || `${s.first_name} ${s.last_name}`.toLowerCase().includes(term),
  );
  const visibleVehicles = (vehicles.data?.vehicles ?? []).filter(
    (v) => !term || (v.fleet_no || v.registration || '').toLowerCase().includes(term),
  );

  const LIST = 'max-h-64 space-y-0.5 overflow-y-auto pr-1';
  const ROW =
    'flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm ' +
    'transition-colors disabled:opacity-40';

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Allocate</h3>
        {/* What dispatch is waiting for, in the dispatcher's words rather than as
            a guard failure after they have already tried to move the case. */}
        <span className="flex items-center gap-1.5 text-xs">
          <Requirement met={crew.length > 0} label="crew" />
          <span className="text-slate-600">·</span>
          <Requirement met={fleet.length > 0} label="vehicle" />
        </span>
      </div>

      {error && (
        <p className="mb-2 rounded bg-red-500/10 px-2 py-1.5 text-sm text-red-300" role="alert">
          {error}
        </p>
      )}

      {/* Assigned, each removable. */}
      {active.length > 0 && (
        <ul className="mb-3 flex flex-wrap gap-1.5">
          {active.map((a) => (
            <li
              key={a.id}
              className="group flex items-center gap-1.5 rounded-full bg-slate-700/70 py-1 pl-2.5 pr-1 text-xs text-slate-100"
            >
              <span className="text-[10px] uppercase tracking-wide text-slate-400">
                {a.role.replace('_', ' ')}
              </span>
              <span className="font-medium">{a.fleet_no ?? personName(a)}</span>
              <button
                type="button"
                disabled={busy}
                onClick={() => release.mutate(a.id)}
                aria-label={`Remove ${a.fleet_no ?? personName(a)}`}
                title="Remove"
                className="flex h-5 w-5 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-red-500/20 hover:text-red-300 disabled:opacity-40"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mb-3">
        <NearestVehicles caseId={caseId} />
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {CREW_ROLES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRole(r.value)}
              className={
                'rounded px-2.5 py-1 text-xs font-medium transition-colors ' +
                (role === r.value
                  ? 'bg-sky-600 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700')
              }
            >
              {r.label}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by name or fleet number"
          className="ml-auto min-w-[12rem] flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <h4 className="mb-1.5 text-xs font-medium text-slate-400">Crew</h4>
          {staff.isLoading && <p className="text-xs text-slate-500">Loading…</p>}
          {staff.data?.staff.length === 0 && (
            <p className="text-xs text-amber-400">
              No staff records yet. Crew must be loaded before a case can be dispatched.
            </p>
          )}
          <div className={LIST}>
            {visibleStaff.map((s) => {
              const on = assignedStaffIds.has(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  disabled={busy || on}
                  onClick={() => assign.mutate({ staffId: s.id, role })}
                  className={`${ROW} ${on ? 'bg-sky-600/15 text-sky-200' : 'text-slate-200 hover:bg-slate-800'}`}
                >
                  <span className="truncate">
                    {s.first_name} {s.last_name}
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <Tier tier={s.capability} />
                    {on ? (
                      <span className="text-[10px] text-sky-300">on this case</span>
                    ) : (
                      s.on_case && <span className="text-[10px] text-amber-400">on a case</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <h4 className="mb-1.5 text-xs font-medium text-slate-400">Vehicles</h4>
          {vehicles.isLoading && <p className="text-xs text-slate-500">Loading…</p>}
          <div className={LIST}>
            {visibleVehicles.map((v) => {
              const on = assignedVehicleIds.has(v.id);
              return (
                <button
                  key={v.id}
                  type="button"
                  disabled={busy || on}
                  onClick={() => assign.mutate({ vehicleId: v.id, role })}
                  className={`${ROW} ${on ? 'bg-sky-600/15 text-sky-200' : 'text-slate-200 hover:bg-slate-800'}`}
                >
                  <span className="truncate font-mono">{v.fleet_no || v.registration}</span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <Tier tier={v.capability_tier} />
                    {on ? (
                      <span className="text-[10px] text-sky-300">on this case</span>
                    ) : (
                      v.on_case && <span className="text-[10px] text-amber-400">on a case</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Requirement({ met, label }: { met: boolean; label: string }) {
  return met ? (
    <span className="text-emerald-400">✓ {label}</span>
  ) : (
    <span className="text-amber-400">needs {label}</span>
  );
}
